import React from 'react';
import { Eye, File, FileArchive, FileSpreadsheet, FileText, Image as ImageIcon } from 'lucide-react';
import {
  getInboxAttachmentPreviewMode,
  getInboxAttachmentVisual,
  withInboxAttachmentInline,
  type InboxAttachmentPreviewTarget,
} from '../inboxAttachmentUtils';

const ICONS = {
  pdf: FileText,
  image: ImageIcon,
  document: FileText,
  spreadsheet: FileSpreadsheet,
  archive: FileArchive,
  file: File,
} as const;

function formatAttachmentSize(size?: number | null): string | null {
  if (typeof size !== 'number' || size <= 0) return null;
  return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(2)} MB` : `${(size / 1024).toFixed(1)} KB`;
}

type Props = {
  filename: string;
  size?: number | null;
  contentType?: string | null;
  previewTarget: InboxAttachmentPreviewTarget | null;
  onOpen: (target: InboxAttachmentPreviewTarget) => void;
};

function PreviewMeta({ filename, sizeLabel }: { filename: string; sizeLabel: string | null }) {
  return (
    <div className="p-2.5">
      <p className="truncate text-xs font-medium text-app-text" title={filename}>
        {filename}
      </p>
      {sizeLabel ? <p className="mt-0.5 text-xs text-app-text-subtle">{sizeLabel}</p> : null}
    </div>
  );
}

export function InboxAttachmentChip({ filename, size, contentType, previewTarget, onOpen }: Props) {
  const visual = getInboxAttachmentVisual(filename, contentType);
  const previewMode = getInboxAttachmentPreviewMode(filename, contentType);
  const Icon = ICONS[visual.kind];
  const sizeLabel = formatAttachmentSize(size);
  const disabled = !previewTarget;
  const inlineUrl = previewTarget ? withInboxAttachmentInline(previewTarget.downloadUrl) : null;

  if (previewMode === 'image' && previewTarget && inlineUrl) {
    return (
      <button
        type="button"
        onClick={() => onOpen(previewTarget)}
        className="group w-full max-w-[11rem] overflow-hidden rounded-lg border border-app-border bg-app-surface text-left transition hover:border-app-text-subtle/50"
        title={filename}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-app-surface-muted">
          <img
            src={inlineUrl}
            alt={filename}
            className="h-full w-full object-cover transition group-hover:scale-[1.02]"
            loading="lazy"
          />
          <span className="pointer-events-none absolute inset-0 bg-black/0 transition group-hover:bg-black/5" />
          <span className="pointer-events-none absolute right-1.5 top-1.5 rounded-md bg-app-surface/85 p-1 text-app-text-subtle opacity-0 shadow-sm backdrop-blur-sm transition group-hover:opacity-100">
            <Eye className="h-3.5 w-3.5" />
          </span>
        </div>
        <PreviewMeta filename={filename} sizeLabel={sizeLabel} />
      </button>
    );
  }

  if (previewMode === 'pdf' && previewTarget) {
    return (
      <button
        type="button"
        onClick={() => onOpen(previewTarget)}
        className="group w-full max-w-[11rem] overflow-hidden rounded-lg border border-app-border bg-app-surface text-left transition hover:border-app-text-subtle/50"
        title={filename}
      >
        <div className="flex aspect-[4/3] items-center justify-center border-b border-app-border bg-app-surface-muted">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-red-100 text-red-700 ring-1 ring-red-200/80">
            <span className="text-xs font-semibold tracking-tight">PDF</span>
          </div>
        </div>
        <PreviewMeta filename={filename} sizeLabel={sizeLabel ? `${sizeLabel} · Vorschau` : 'Vorschau'} />
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => previewTarget && onOpen(previewTarget)}
      className="group flex w-full max-w-[min(100%,18rem)] items-center gap-2.5 rounded-xl border border-app-border/90 bg-app-surface/95 px-2.5 py-2 text-left transition hover:border-app-text-subtle/50 hover:bg-app-surface disabled:cursor-default disabled:opacity-80 disabled:hover:border-app-border/90"
      title={filename}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1 ${visual.badgeClassName}`}
      >
        {visual.kind === 'pdf' || visual.kind === 'image' ? (
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        ) : (
          <span className="text-xs font-semibold leading-none tracking-tight">{visual.label}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-app-text">{filename}</p>
        <p className="mt-0.5 text-xs text-app-text-subtle">
          {[sizeLabel, visual.canPreview ? 'Vorschau' : 'Download'].filter(Boolean).join(' · ')}
        </p>
      </div>
      {visual.canPreview && previewTarget ? (
        <Eye className="h-3.5 w-3.5 shrink-0 text-app-text-subtle transition group-hover:text-pipedrive-blue" />
      ) : null}
    </button>
  );
}
